import * as React from 'react'
import * as Sentry from '@sentry/react'
import { loader as gqlLoader } from 'graphql.macro'
import { useQuery } from '@apollo/client'
import { usePostHog } from 'posthog-js/react'
import { toast } from 'react-toastify'
import {
  completePendingSwitch,
  ORCHID_NOT_ROUTED_MESSAGE,
  reconcileExperienceCookie,
} from '../../helpers/orchidExperience'
import {
  CurrentUserQueryQuery,
  CurrentUserQueryQueryVariables,
} from '../../../graphql/@types/schema'
const CurrentUserQuery = gqlLoader('./data/CurrentUserQuery.graphql')
const UpdateMySettingsMutation = gqlLoader('../data/UpdateMySettingsMutation.graphql')

type SetSentryUserContextParams = {
  id: string
  email: string
  subdomain: string
}

const setSentryUserContext = (userContext: SetSentryUserContextParams) => {
  Sentry.configureScope(function (scope) {
    scope.setUser(userContext)
  })
}

const useCurrentUser = () => {
  const posthog = usePostHog()
  // The rollback is in flight, so the cached preference on this load still says
  // Orchid. Without this the reconcile below writes the cookie the fail-safe
  // just removed and reloads into the same dead end.
  const hasUndoneSwitch = React.useRef(false)
  // The client comes off the query rather than a second `useMutation`: this hook
  // renders on nearly every page, and a mutation document verified at render
  // would fire in every suite that stubs the GraphQL loader.
  const { client, data, error, loading } = useQuery<
    CurrentUserQueryQuery,
    CurrentUserQueryQueryVariables
  >(CurrentUserQuery)

  // Rendering here at all is the answer a switch was waiting for: the reader
  // asked for Orchid, the reload came back to legacy, so the edge serves this
  // address from legacy whatever the cookie says. The switch is undone rather
  // than pushed through — the cookie is already off, so the durable preference
  // has to follow it, or every later load reconciles the cookie straight back.
  React.useEffect(() => {
    const result = completePendingSwitch({
      getCookie: () => document.cookie,
      setCookie: (value) => {
        document.cookie = value
      },
      isSecure: window.location.protocol === 'https:',
    })
    if (result === null) return
    hasUndoneSwitch.current = true

    void client.mutate({
      mutation: UpdateMySettingsMutation,
      variables: { settings: { preferences: { isOrchidWebEnabled: null } } },
    })
    toast.error(ORCHID_NOT_ROUTED_MESSAGE, { autoClose: false })
    posthog?.capture('orchid_web_experience_change_failed', {
      experience: 'orchid',
      source_app: 'legacy',
      reason: 'not_routed_after_reload',
    })
  }, [client, posthog])

  React.useEffect(() => {
    if (hasUndoneSwitch.current) return
    if (loading === false && data != null && data.currentUser != null) {
      const sentryInfo = {
        id: data.currentUser.id,
        email: data.currentUser.email as string,
        subdomain: data.currentUser.organization.subdomain,
      }
      setSentryUserContext(sentryInfo)

      // The account remembers the choice; the cookie is only how that reaches
      // the edge, which decides before any app code runs. Reconciling on every
      // load is what makes the account authoritative — a device holding a stale
      // cookie is corrected rather than obeyed, which is why enabling the
      // switch on a laptop now reaches the phone that scanned a smart code.
      reconcileExperienceCookie(
        {
          isOrchidChosenByUser:
            data.currentUser.settings?.preferences?.attributes?.isOrchidWebEnabled?.value ?? null,
          // The organization default is not queried here yet; once it is, an
          // org-wide enable reaches every device on its next load rather than
          // only devices that happen to have no cookie.
          isOrchidDefaultForOrg: null,
        },
        {
          getCookie: () => document.cookie,
          setCookie: (value) => {
            document.cookie = value
          },
          isSecure: window.location.protocol === 'https:',
          reload: () => window.location.reload(),
        },
        // A correction means this reader was in the WRONG app until now. Counted
        // because nothing else can see it: an access log records what a request
        // carried, and a stranded reader carried no experience cookie, which
        // looks exactly like never having opted in.
        ({ from, to }) => {
          posthog?.capture('orchid_web_experience_reconciled', {
            from,
            to,
            source_app: 'legacy',
          })
        },
      )
    }
  }, [data, error, loading, posthog])

  return { currentUser: data && data.currentUser, loading, error }
}

export type CurrentUser = CurrentUserQueryQuery['currentUser']
export type PartialCurrentUserWithRequiredRole = Partial<CurrentUser> & {
  role: CurrentUserQueryQuery['currentUser']['role']
}
export default useCurrentUser
